import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, query, where, Timestamp } from 'firebase/firestore';
import { diaEnZona } from '../utils/ventasResumen';
import { zonaDelNegocio, hayAgendaConectada, reunionesCreadasHoy } from '../utils/reunionesResumen';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { useOrganization } from '../context/OrganizationContext';
import { useChannelsOperational } from './useChannelOperational';

export default function useDashboardData() {
  const { context, loading: contextLoading } = useOrganization();

  // Messaging channels' OPERATIONAL status (single source of truth). tri-state per canal:
  // true|false|undefined(loading). whatsapp stays on its own numeros path (below).
  const operational = useChannelsOperational(['instagram', 'messenger', 'tiktok']);

  const [state, setState] = useState({
    loading: true,
    channelCount: 0,
    botCount: 0,
    conversationsToday: 0,
    botRepliesToday: 0,
    // `null` = todavía no se sabe. Se distingue de `false` a propósito: mientras
    // carga, la tarjeta de ventas no debe parpadear ni afirmar que no hay tienda.
    ecommerceActivo: null,
    // Config de la organización, para la zona horaria del negocio. Sale del
    // MISMO snapshot que `ecommerceActivo`: no cuesta una lectura más.
    orgConfig: null,
    // `botId -> config del bot`. Un listener por bot; ver el comentario de abajo.
    botConfigs: {},
    botIds: [],
    bookingsRecientes: [],
  });

  useEffect(() => {
    if (contextLoading) return;

    const auth = getAuth();
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    const basePath = context.isOrganization && context.orgId
      ? `organizations/${context.orgId}`
      : `clientes/${uid}`;

    const unsubs = [];

    // ¿Hay una tienda conectada? El dashboard no lo sabía: `integrations` sólo
    // traía los canales de mensajería. Es la MISMA condición que ya usan
    // `BotIntegrationsPanel` y `EcommerceIntegration` —`ecommerce.provider` en
    // `{base}/integrations/config`— sobre un doc que este hook ya sabe
    // direccionar, así que no hace falta endpoint ni regla nueva.
    unsubs.push(onSnapshot(doc(db, `${basePath}/integrations/config`), (snap) => {
      const cfg = snap.data() || {};
      setState(prev => ({ ...prev, ecommerceActivo: !!cfg.ecommerce?.provider, orgConfig: cfg }));
    }, () => {
      // Sin permiso o sin doc: se asume que no hay tienda. Es el lado seguro —
      // no mostrar una tarjeta vacía es mejor que mostrarla sin poder llenarla.
      setState(prev => ({ ...prev, ecommerceActivo: false, orgConfig: {} }));
    }));

    // Reuniones recientes, para el contador de la barra de estado.
    //
    // Se traen las últimas 48 h por `createdAt` —un solo campo, así que usa el
    // índice automático y no hace falta declarar uno compuesto— y el día lo
    // decide `reunionesCreadasHoy` con la zona del negocio. La ventana es amplia
    // a propósito: cualquier huso cabe con margen, y el filtro fino es en cliente.
    const hace48h = Timestamp.fromDate(new Date(Date.now() - 48 * 60 * 60 * 1000));
    const bookingsQ = query(collection(db, basePath, 'bookings'), where('createdAt', '>=', hace48h));
    unsubs.push(onSnapshot(bookingsQ, (snap) => {
      setState(prev => ({ ...prev, bookingsRecientes: snap.docs.map(d => d.data()) }));
    }, () => {
      setState(prev => ({ ...prev, bookingsRecientes: [] }));
    }));

    // Listen to numeros collection for channel count
    const numerosRef = collection(db, basePath, 'numeros');
    unsubs.push(onSnapshot(numerosRef, (snap) => {
      setState(prev => ({ ...prev, channelCount: snap.size }));
    }, () => {
      setState(prev => ({ ...prev, channelCount: 0 }));
    }));

    // Listen to bots collection
    //
    // UN LISTENER POR BOT PARA SABER UN BOOLEANO. El calendario vive en
    // `bots/{botId}/integrations/config` desde el rediseño per-bot, así que "¿hay
    // alguna agenda conectada?" no se puede responder con una lectura como el
    // gate de e-commerce: hay que bajar un nivel por cada bot.
    //
    // Con dos bots es trivial. Con veinte son veinte listeners para un booleano,
    // y el patrón se va a repetir con cada integración que migre a per-bot. Está
    // anotado en POST-LANZAMIENTO: el día que moleste, la solución no es
    // optimizar esto sino desnormalizar una marca en el doc de la organización.
    const botsRef = collection(db, basePath, 'bots');
    const unsubsBots = new Map();
    unsubs.push(onSnapshot(botsRef, (snap) => {
      const ids = snap.docs.map(d => d.id);
      setState(prev => ({ ...prev, botCount: snap.size, botIds: ids }));

      // Se desuscribe de los bots que ya no están, para no dejar listeners vivos
      // sobre documentos borrados.
      for (const [id, fn] of unsubsBots) {
        if (!ids.includes(id)) { fn(); unsubsBots.delete(id); }
      }
      for (const id of ids) {
        if (unsubsBots.has(id)) continue;
        unsubsBots.set(id, onSnapshot(doc(db, `${basePath}/bots/${id}/integrations/config`), (cfgSnap) => {
          setState(prev => ({ ...prev, botConfigs: { ...prev.botConfigs, [id]: cfgSnap.data() || {} } }));
        }, () => {
          setState(prev => ({ ...prev, botConfigs: { ...prev.botConfigs, [id]: {} } }));
        }));
      }
    }, () => {
      setState(prev => ({ ...prev, botCount: 0, botIds: [] }));
    }));
    unsubs.push(() => unsubsBots.forEach(fn => fn()));

    // Listen to chats for today's conversation count
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const todayTimestamp = Timestamp.fromDate(startOfToday);

    const chatsRef = collection(db, basePath, 'chats');
    const todayQuery = query(chatsRef, where('lastTs', '>=', todayTimestamp));
    unsubs.push(onSnapshot(todayQuery, (snap) => {
      setState(prev => ({ ...prev, conversationsToday: snap.size, loading: false }));
    }, () => {
      setState(prev => ({ ...prev, conversationsToday: 0, loading: false }));
    }));

    // Listen to parent doc for usage counters (botRepliesToday)
    const parentRef = doc(db, basePath);
    unsubs.push(onSnapshot(parentRef, (snap) => {
      const usage = snap.data()?.usage || {};
      setState(prev => ({ ...prev, botRepliesToday: usage.botRepliesToday || 0 }));
    }, () => {}));

    // Connection status for instagram/messenger/tiktok now comes from the operational
    // helper (channelAccounts + MESSAGING_CAPABLE), NOT the singleton integrations/{canal}.

    return () => unsubs.forEach(fn => fn());
  }, [contextLoading, context.isOrganization, context.orgId]);

  // whatsapp from numeros count (boolean); ig/messenger/tiktok OPERATIONAL (tri-state
  // true|false|undefined). undefined = loading → consumers render NEUTRAL (no red).
  // gmail/calendar are not shown on the dashboard.
  const integrations = {
    whatsapp: state.channelCount > 0,
    instagram: operational.instagram,
    messenger: operational.messenger,
    tiktok: operational.tiktok,
  };

  // Agenda: hay al menos un bot con calendario conectado. `null` mientras falte
  // algún bot por responder — la barra no debe afirmar que no hay agenda
  // mientras está averiguando.
  const calendarActivo = hayAgendaConectada(state.botConfigs, state.botIds);

  // Reuniones creadas HOY que siguen confirmadas. El día se corta con la zona del
  // negocio y con `diaEnZona`, la MISMA función que usa la tarjeta de ventas: dos
  // números de la misma pantalla no pueden tener dos medianoches distintas.
  //
  // (Las otras dos de esta barra sí las tienen, y es preexistente:
  // `conversationsToday` usa la medianoche del NAVEGADOR y `botRepliesToday` la
  // del CRON que lo resetea. Está anotado en POST-LANZAMIENTO; no se unifica acá
  // porque tocar ese cron es riesgo real por una diferencia que hoy, con todos
  // los usuarios en Chile, no le afecta a nadie.)
  const zonaNegocio = zonaDelNegocio(state.orgConfig);
  const reunionesHoy = reunionesCreadasHoy(
    state.bookingsRecientes, diaEnZona(new Date(), zonaNegocio), zonaNegocio
  );

  // Total OPERATIONAL channels = WhatsApp numbers + each operational messaging channel.
  // Only `=== true` counts; loading (undefined) and non-operational do NOT.
  const channelCount = state.channelCount
    + (operational.instagram === true ? 1 : 0)
    + (operational.messenger === true ? 1 : 0)
    + (operational.tiktok === true ? 1 : 0);

  const hasChannels = channelCount > 0;
  const hasBots = state.botCount > 0;
  const isOnboarding = !hasChannels && !hasBots;
  const setupComplete = hasChannels && hasBots;

  return {
    ...state,
    channelCount,
    integrations,
    calendarActivo,
    reunionesHoy,
    hasChannels,
    hasBots,
    isOnboarding,
    setupComplete,
  };
}
